// prefabReflection — mirror GASS prefab edits into convorcher (ENG-834 P2).
//
// A prefab is two halves with one key: the ANIMATION BUNDLE lives in AAM
// (what plays — keyed (character slug, tag)), and the PHRASE-TRIGGER row lives
// in convorcher (when it fires + what the LLM says — keyed (characterName,
// name)). Sessions only load prefabs linked to their story, so a reflected
// row must also be story-linked or it never enters validPrefabs.
//
// Contract (deliberate):
//   - name == tag verbatim for new prefabs. No renames of existing convorcher
//     prefabs ever — the wire (NewResponse.trigger) must keep emitting
//     historical names (Unreal reads them too; see ENG-839). Legacy PascalCase
//     rows are still FOUND (findPrefabRow falls back to the runtime's
//     normalizeTriggerToTag bridge) so edits update them in place instead of
//     minting snake_case duplicates.
//   - characterName == the GASS character slug. Both sides already lowercase:
//     convorcher scopes by LOWER(characters.name) (migration 00022) and the
//     brain panel resolves characters the same way (brainVoiceStore).
//   - Auto-link to ALL of the character's stories — the precedent set by the
//     seeded prefabs (each linked to myra_v1 AND myra_v4), and a superset of
//     the session rule (latest story). Manual curation stays possible in the
//     convorcher panel; setStories here unions, never removes.
//   - GASS's `text` maps to convorcher `alternate` (the canned line for
//     repeat-fires). `phrases` / `notFor` (when the LLM should fire it) are
//     authored in the GASS Triggering UI (ENG-843) as string arrays; passing
//     them as undefined preserves the row's existing values — reflection never
//     clobbers authored triggers on a playback-only save.
//
// Failure policy: reflection runs AFTER a successful AAM save and must not
// undo/blank it — but it fails LOUDLY (console.error + rethrow to the caller's
// .catch) because a prefab that silently never fires is the exact bug this
// ticket exists to kill.

import { convorcherAdmin } from './convorcherAdmin.js'
import { normalizeTriggerToTag } from './prefabTrigger.js'

const norm = (s) => String(s ?? '').trim().toLowerCase()

// Convorcher returns phrases/notFor as jsonb string arrays (PRs #98/#99);
// rows written before the migration backfill may still surface a legacy
// string. Normalize ONLY for comparison / display — writes pass the author's
// value verbatim (the API dual-accepts string-or-array during the transition).
export function asPhraseList(v) {
  if (Array.isArray(v)) return v
  if (v == null) return []
  const s = String(v).trim()
  return s ? [s] : []
}

/** Pure: same phrase list? (order-sensitive — arrays are authored order). */
export function samePhraseList(a, b) {
  const A = asPhraseList(a)
  const B = asPhraseList(b)
  return A.length === B.length && A.every((x, i) => x === B[i])
}

/**
 * Pure: pick the convorcher prefab row matching (slug, tag), or null.
 * Rows from a pre-migration server carry no characterName — the caller
 * detects that separately (serverHasCharacterScope); here undefined never
 * matches, so we can't silently adopt another character's row.
 *
 * Match order: verbatim name === tag first (the ENG-834 authoring
 * convention), then normalized equality via the SAME PascalCase→snake_case
 * bridge the runtime trigger resolution uses — legacy seed rows keep their
 * historical names ("GoodLuckResearch" for tag good_luck_research; the wire
 * must keep emitting them), and without this fallback the Triggering editor
 * would show them phrase-less and a save would mint a duplicate row.
 */
export function findPrefabRow(rows, slug, tag) {
  const list = Array.isArray(rows) ? rows : []
  const mine = list.filter(r => norm(r?.characterName) === norm(slug))
  return (
    mine.find(r => r?.name === tag) ??
    mine.find(r => normalizeTriggerToTag(r?.name) === normalizeTriggerToTag(tag)) ??
    null
  )
}

/** Pure: does the server speak per-character prefabs (migration 00022)? */
export function serverHasCharacterScope(rows) {
  const list = Array.isArray(rows) ? rows : []
  if (list.length === 0) return true // nothing to disambiguate — safe either way
  return list.some(r => r && r.characterName !== undefined)
}

/** Pure: story ids belonging to a character, by lowercased name match. */
export function storyIdsForCharacter(stories, slug) {
  const list = Array.isArray(stories) ? stories : []
  return list.filter(s => norm(s?.characterName) === norm(slug) && s?.id).map(s => s.id)
}

/** Pure: merged story-id set — reflection only ever ADDS links. */
export function unionStoryIds(existingRefs, wanted) {
  const have = (Array.isArray(existingRefs) ? existingRefs : []).map(s => s?.id).filter(Boolean)
  return [...new Set([...have, ...wanted])]
}

/**
 * Pure: compute the write plan for an upsert so the network layer stays a
 * dumb executor (and this stays unit-testable without mocks).
 * Returns { action: 'create'|'update'|'skip', ... }.
 *
 * phrases / notFor (ENG-843): string[] = set (the Triggering UI authored
 * them), undefined = preserve whatever the row already holds (the pre-843
 * behavior — a playback-only save must never clobber authored triggers).
 */
export function planUpsert({ rows, stories, slug, tag, text, phrases, notFor }) {
  if (!serverHasCharacterScope(rows)) {
    return { action: 'skip', reason: 'convorcher server has no per-character prefabs yet (migration 00022 not deployed)' }
  }
  const wantedStories = storyIdsForCharacter(stories, slug)
  const existing = findPrefabRow(rows, slug, tag)
  if (!existing) {
    return {
      action: 'create',
      body: {
        characterName: norm(slug),
        name: tag,
        phrases: phrases ?? [],
        alternate: text ?? '',
        notFor: notFor ?? [],
      },
      storyIds: wantedStories,
    }
  }
  // Update in place: preserve panel-authored phrases/notFor unless this save
  // explicitly authored them; only take the GASS text as alternate when the
  // author actually wrote one.
  const nextPhrases = phrases !== undefined ? phrases : (existing.phrases ?? [])
  const nextNotFor = notFor !== undefined ? notFor : (existing.notFor ?? [])
  const alternate = text ? text : (existing.alternate ?? '')
  const storyIds = unionStoryIds(existing.stories, wantedStories)
  const bodyChanged =
    alternate !== (existing.alternate ?? '') ||
    !samePhraseList(nextPhrases, existing.phrases) ||
    !samePhraseList(nextNotFor, existing.notFor)
  const linksChanged = storyIds.length !== (existing.stories?.length ?? 0)
  if (!bodyChanged && !linksChanged) return { action: 'skip', reason: 'already reflected' }
  return {
    action: 'update',
    id: existing.id,
    body: { phrases: nextPhrases, alternate, notFor: nextNotFor },
    bodyChanged,
    storyIds,
    linksChanged,
  }
}

/**
 * Reflect a GASS prefab create/edit into convorcher. Returns the executed
 * plan. Throws on network/API failure (callers log-and-continue: the AAM save
 * already succeeded and must not be rolled back over a reflection failure).
 */
export async function reflectPrefabUpsert({ slug, tag, text, phrases, notFor }) {
  const [rows, stories] = await Promise.all([convorcherAdmin.listPrefabs(), convorcherAdmin.listStories()])
  const plan = planUpsert({ rows, stories, slug, tag, text, phrases, notFor })
  if (plan.action === 'skip') {
    if (plan.reason !== 'already reflected') {
      console.error(`[prefabReflection] NOT reflected "${tag}" for "${slug}": ${plan.reason}`)
    }
    return plan
  }
  if (plan.action === 'create') {
    const created = await convorcherAdmin.createPrefab(plan.body)
    if (plan.storyIds.length) await convorcherAdmin.setPrefabStories(created.id, plan.storyIds)
    console.log(`[prefabReflection] created convorcher prefab "${tag}" for "${slug}" (stories: ${plan.storyIds.length})`)
    return { ...plan, id: created.id }
  }
  if (plan.bodyChanged) await convorcherAdmin.updatePrefab(plan.id, plan.body)
  if (plan.linksChanged) await convorcherAdmin.setPrefabStories(plan.id, plan.storyIds)
  console.log(`[prefabReflection] updated convorcher prefab "${tag}" for "${slug}"`)
  return plan
}

/** Reflect a GASS prefab delete. Only removes the row scoped to this slug. */
export async function reflectPrefabDelete({ slug, tag }) {
  const rows = await convorcherAdmin.listPrefabs()
  if (!serverHasCharacterScope(rows)) {
    console.error(`[prefabReflection] NOT deleting "${tag}" for "${slug}": server has no per-character prefabs yet`)
    return { action: 'skip' }
  }
  const existing = findPrefabRow(rows, slug, tag)
  if (!existing) return { action: 'skip', reason: 'no convorcher row' }
  await convorcherAdmin.deletePrefab(existing.id)
  console.log(`[prefabReflection] deleted convorcher prefab "${tag}" for "${slug}"`)
  return { action: 'delete', id: existing.id }
}
